import {
  type FormAudience,
  type FormField,
  type FormSchema,
  isFieldInAudience,
} from './form-schema.js';

/**
 * The field types whose answer may decide a price. A `select` or a `radio`
 * stores one option value and a `checkbox` a tick, so a price row can name the
 * answer it is for exactly; a `multiselect` stores a set, and a text box
 * anything at all, so neither can.
 */
export const PRICE_FIELD_TYPES = ['select', 'radio', 'checkbox'] as const;

export type PriceFieldType = (typeof PRICE_FIELD_TYPES)[number];

/**
 * How many of its own answers a campaign may price by. A conditional price
 * names every one of them, so each more multiplies the combinations an
 * operator enters; a campaign needing more is describing separate campaigns.
 */
export const MAX_PRICE_FIELD_KEYS = 8;

/** An option value for a `select` or `radio`, `true` or `false` for a `checkbox`. */
export type PriceConditionValue = string | boolean;

/**
 * The answers one price row is for, by field key. `{}` is the unconditional
 * price; anything else names **every** price-deciding key of the campaign,
 * which is what makes a tie between two rows of one kind impossible — they can
 * both match one donation only by being equal, and the database's unique
 * constraint refuses two equal ones.
 */
export type PriceConditions = Readonly<Record<string, PriceConditionValue>>;

export function isPriceField(
  field: Pick<FormField, 'type'>,
): field is Extract<FormField, { type: PriceFieldType }> {
  return (PRICE_FIELD_TYPES as readonly string[]).includes(field.type);
}

/** The fields a campaign may name as price-deciding, in schema order. */
export function priceFieldCandidates(
  schema: FormSchema,
): Extract<FormField, { type: PriceFieldType }>[] {
  return schema.fields.filter(isPriceField);
}

export type PriceFieldKeyProblem = {
  readonly problem: 'field_unknown' | 'field_not_priceable' | 'field_named_twice';
  readonly fieldKey: string;
};

/**
 * The rule the trigger `campaigns_price_keys_name_published_fields` holds for
 * the keys themselves, asked first so a refusal names the key rather than a
 * trigger: every key names a published `select`, `radio` or `checkbox`, and
 * none twice. Asked when the keys are set and when a schema is published,
 * because either can break it.
 */
export function priceFieldKeyProblem(
  keys: readonly string[],
  schema: FormSchema,
): PriceFieldKeyProblem | null {
  const seen = new Set<string>();
  for (const fieldKey of keys) {
    if (seen.has(fieldKey)) return { problem: 'field_named_twice', fieldKey };
    seen.add(fieldKey);
    const field = schema.fields.find((candidate) => candidate.key === fieldKey);
    if (!field) return { problem: 'field_unknown', fieldKey };
    if (!isPriceField(field)) return { problem: 'field_not_priceable', fieldKey };
  }
  return null;
}

export function isUnconditional(conditions: PriceConditions | null | undefined): boolean {
  return conditions === null || conditions === undefined || Object.keys(conditions).length === 0;
}

export type PriceConditionProblem =
  | { readonly problem: 'no_price_fields' }
  | { readonly problem: 'not_every_price_field'; readonly fieldKeys: readonly string[] }
  | { readonly problem: 'field_unknown'; readonly fieldKey: string }
  | {
      readonly problem: 'answer_not_offered';
      readonly fieldKey: string;
      readonly answer: PriceConditionValue;
    };

/**
 * What the function `campaign_price_conditions_problem` refuses in the
 * database, asked first so a screen is told which field and which answer:
 * conditions name exactly the campaign's price-deciding keys, a choice with one
 * of its own option values and a box with `true` or `false`. `{}` is always
 * valid — it is the unconditional price.
 */
export function priceConditionProblem(
  conditions: PriceConditions,
  keys: readonly string[],
  schema: FormSchema,
): PriceConditionProblem | null {
  if (isUnconditional(conditions)) return null;
  if (keys.length === 0) return { problem: 'no_price_fields' };
  const named = Object.keys(conditions).sort();
  const wanted = [...keys].sort();
  if (named.length !== wanted.length || named.some((key, at) => key !== wanted[at])) {
    return { problem: 'not_every_price_field', fieldKeys: [...keys] };
  }
  for (const fieldKey of keys) {
    const answer = conditions[fieldKey] as PriceConditionValue;
    const field = schema.fields.find((candidate) => candidate.key === fieldKey);
    if (!field || !isPriceField(field)) return { problem: 'field_unknown', fieldKey };
    const offered =
      field.type === 'checkbox'
        ? typeof answer === 'boolean'
        : typeof answer === 'string' && field.options.some((option) => option.value === answer);
    if (!offered) return { problem: 'answer_not_offered', fieldKey, answer };
  }
  return null;
}

/**
 * Two condition sets are the same price's when they name the same answers,
 * whatever order the keys were written in — which is how `jsonb` compares them
 * in the unique constraint too.
 */
export function samePriceConditions(left: PriceConditions, right: PriceConditions): boolean {
  const leftKeys = Object.keys(left);
  if (leftKeys.length !== Object.keys(right).length) return false;
  return leftKeys.every((key) => Object.hasOwn(right, key) && right[key] === left[key]);
}

function answersOf(formData: unknown): Readonly<Record<string, unknown>> {
  if (formData === null || typeof formData !== 'object' || Array.isArray(formData)) return {};
  return formData as Record<string, unknown>;
}

/**
 * A tick is `true` in a stored `form_data`, and the text `true` when it
 * arrives in a quote's query string, which can carry nothing else; a validated
 * `select` never stores a boolean, so neither reading can be mistaken for the
 * other's.
 */
function isTicked(value: unknown): boolean {
  return value === true || value === 'true';
}

/**
 * Whether a donation's answers are the ones a price row is for. A choice must
 * give exactly the option the row names; a box must be ticked for `true` and
 * not ticked — unticked or unanswered — for `false`. The unconditional price
 * matches everything.
 */
export function priceConditionsMatch(conditions: PriceConditions, formData: unknown): boolean {
  const answers = answersOf(formData);
  return Object.entries(conditions).every(([fieldKey, wanted]) =>
    typeof wanted === 'boolean'
      ? isTicked(answers[fieldKey]) === wanted
      : answers[fieldKey] === wanted,
  );
}

/** Where a conditional price row belongs: to the season, or to one assignment of it. */
export type PriceLevel = 'season' | 'assignment';

export type AnswerPrice<P> = P & {
  readonly level: PriceLevel;
  readonly answerConditions: PriceConditions;
};

export type PriceCandidates<P> = {
  /** The season's unconditional price in the currency — required by the database for any other row. */
  readonly seasonPrice: P | null;
  /** The chosen assignment's unconditional override, when one was named and it has one. */
  readonly assignmentPrice: P | null;
  /** Conditional rows of the season and of the chosen assignment, in the currency. */
  readonly answerPrices: readonly AnswerPrice<P>[];
};

/**
 * **The specificity order**, most specific first, the first that exists wins:
 *
 * 1. the chosen assignment's price for these answers;
 * 2. the season's price for these answers;
 * 3. the chosen assignment's unconditional price;
 * 4. the season's unconditional price.
 *
 * A yurt's own figure for exactly this case beats the season's figure for it,
 * and any figure for the case beats a figure that ignores it: an urgent
 * slaughter priced for the whole season still outranks a yurt's normal price,
 * because the answer is what the donor asked for and the yurt only where.
 *
 * There is no tie to break inside a step. A conditional row names every
 * price-deciding key, so two rows of one level matching one donation have
 * equal conditions, and `campaign_period_prices_one_price_per_answers` refuses
 * the second; this function therefore takes the one match and never chooses
 * between two. An unanswered form matches no conditional row and falls to
 * step 3 or 4, which the database always holds.
 */
export function resolvePrice<P>(candidates: PriceCandidates<P>, formData: unknown): P | null {
  const matching = (level: PriceLevel) =>
    candidates.answerPrices.find(
      (row) =>
        row.level === level &&
        !isUnconditional(row.answerConditions) &&
        priceConditionsMatch(row.answerConditions, formData),
    );
  return (
    matching('assignment') ??
    matching('season') ??
    candidates.assignmentPrice ??
    candidates.seasonPrice
  );
}

/**
 * Only the answers an audience could have given. The public quote and the
 * basket are sent a donor's answers, and a donor never sees an `adminOnly` or
 * `portalVisible: false` field, so an answer to one is dropped before it can
 * select a price — a price conditioned on it is never disclosed publicly, and
 * never charged there either.
 */
export function answersInAudience(
  schema: { fields?: unknown } | null | undefined,
  formData: unknown,
  audience: FormAudience,
): Record<string, unknown> {
  const answers = answersOf(formData);
  const fields = Array.isArray(schema?.fields) ? (schema.fields as unknown[]) : [];
  const kept: Record<string, unknown> = {};
  for (const field of fields) {
    if (field === null || typeof field !== 'object') continue;
    const { key } = field as { key?: unknown };
    if (typeof key !== 'string' || !Object.hasOwn(answers, key)) continue;
    if (!isFieldInAudience(field as Parameters<typeof isFieldInAudience>[0], audience)) continue;
    kept[key] = answers[key];
  }
  return kept;
}
